<template>
  <template
    v-if="
      current.account.isFetched &&
      current.accountContacts.isFetched &&
      current.customerAccounts.isFetched
    "
  >
    <div>
      <CustomerAccountInfo />
      <div
        v-if="psrAlert && !isPsrAlertLoading"
        class="d-flex align-center mb-2"
        data-testid="sp-psr-alert"
      >
        <v-sheet
          color="warning"
          class="sp-psr-alert rounded text-body-2 py-1 px-2"
        >
          <SoTooltip :disabled="psrServicesList.length === 0" location="start">
            <SoIcon icon="human" size="small" />
            {{ psrAlert }}
            <template #custom>
              <div class="pa-2">
                <strong>Services</strong>
                <ul class="bulletpoint-list">
                  <li v-for="service in psrServicesList" :key="service.id">
                    {{ psrServiceTitle(service) }}
                  </li>
                </ul>
              </div>
            </template>
          </SoTooltip>
        </v-sheet>
      </div>

      <div class="sp-button-grid">
        <SoButton
          title="Submit Reading"
          full-width
          @click="isSubmitReadingModalOpen = true"
        />
        <SoButton
          title="Priority Services"
          data-testid="priority-services-btn"
          full-width
          @click="isPsrModalOpen = true"
        />
        <SoButton
          title="Open in Junifer"
          data-testid="open-in-junifer-btn"
          full-width
          @click="openInJunifer()"
        />
        <SoButton
          title="Online Account"
          full-width
          target="_blank"
          :loading="isSigningIn"
          :disabled="isSigningIn"
          @click="signInToMyAccount()"
        />
        <SoButton
          v-if="showCancelSwitchButton"
          type="danger"
          title="Cancel Switch"
          @click="isCancelSwitchModalOpen = true"
        />
      </div>
    </div>

    <SoDivider class="my-6" />

    <template v-if="gdprFields.length">
      <ConfirmGdprDetails :gdpr-fields="gdprFields" />
      <SoDivider class="my-6" />
    </template>

    <OnlineAccountEmail
      class="mx-2"
      :users="onlineAccountUsers"
      :primary-contact-email="primaryContact?.email"
      :is-loading="isOnlineAccountLoading"
    />

    <SoDivider class="my-6" />

    <h6 class="font-weight-medium mx-2" data-testid="sp-contacts-heading">
      Contacts
    </h6>

    <div v-if="primaryContact" class="mx-2 mt-5">
      <div class="w-100 d-flex align-center justify-space-between">
        <h6 class="font-weight-medium">{{ primaryContact?.fullName }}</h6>
        <SoCopyToClipboard :plain-text="primaryContact?.fullName">
          <template #button="{ copyToClipboard }">
            <v-btn
              type="sidebar-icon"
              size="x-small"
              variant="plain"
              icon="copy sm"
              @click.stop="copyToClipboard"
            />
          </template>
        </SoCopyToClipboard>
      </div>

      <div class="d-flex justify-space-between align-center">
        <span class="text-medium-emphasis">Primary</span>
        <SoTooltip text="Edit">
          <SoButton
            type="sidebar-icon"
            icon="ellipsis"
            data-testid="open-contact-details-modal"
            :disabled="current.accountContacts.isFetching"
            @click="openContactModal(primaryContact.juniferContactId)"
          />
        </SoTooltip>
      </div>
      <div
        data-testid="sp-primary-email"
        class="d-flex justify-space-between flex-wrap content-spacing"
      >
        <div ref="primary-contact-email" class="d-inline-block text-truncate">
          {{ primaryContact?.email }}
        </div>
        <div class="d-flex">
          <SoCopyToClipboard :plain-text="primaryContact.email">
            <template #button="{ copyToClipboard }">
              <SoButton
                type="sidebar-icon"
                icon="copy sm"
                @click="copyToClipboard"
              />
            </template>
          </SoCopyToClipboard>
          <SoTooltip text="Email delivery status">
            <SoButton
              type="sidebar-icon"
              icon="message-processing sm"
              data-testid="open-mandrill-modal-btn"
              @click="openMandrillEmailModal(primaryContact.email || '')"
            />
          </SoTooltip>
        </div>
      </div>
      <CustomerPhoneNumberList
        :phone-numbers="[primaryContact.phone1, primaryContact.phone2]"
      />
      <div class="content-spacing">
        <div>Communication Delivery: {{ commsDelivery }}</div>
        <div class="d-flex justify-space-between align-center">
          <span>Billing Delivery: {{ billingDelivery }}</span>
          <SoTooltip
            v-if="canManageBillDelivery"
            :text="manageBillDelivery.tooltip"
            :disabled="!manageBillDelivery.disabled"
          >
            <SoButton
              type="sidebar-icon"
              icon="pencil sm"
              data-testid="manage-bill-delivery-btn"
              :disabled="manageBillDelivery.disabled"
              @click="isManageBillDeliveryModalOpen = true"
            />
          </SoTooltip>
        </div>
        <div class="my-2">
          <SoButton
            title="Add Contact"
            prepend-icon="plus"
            :disabled="current.accountContacts.isFetching"
            class="mr-2"
            @click="openContactModal()"
          />
          <SoButton
            icon="delete"
            :disabled="current.accountContacts.isFetching"
            @click="openContactRemoveModal(primaryContact.juniferContactId)"
          />
        </div>
        <SoButton
          title="Add Review"
          prepend-icon="plus"
          :disabled="current.accountContacts.isFetching"
          @click="openReviewPeriodsModal"
        />
      </div>
    </div>

    <SoExpansionPanels
      v-for="contact in secondaryContacts"
      :key="contact.juniferContactId"
      class="mt-4"
    >
      <SoExpansionPanel>
        <template #title>
          <div class="w-100 d-flex align-center justify-space-between">
            <h6 class="font-weight-medium">{{ contact.fullName }}</h6>
            <SoCopyToClipboard :plain-text="contact.fullName">
              <template #button="{ copyToClipboard }">
                <v-btn
                  type="sidebar-icon"
                  size="x-small"
                  variant="plain"
                  icon="copy sm"
                  @click.stop="copyToClipboard"
                />
              </template>
            </SoCopyToClipboard>
          </div>
        </template>
        <template #alwaysVisible>
          <div class="d-flex justify-space-between align-center">
            <span class="text-medium-emphasis">Secondary</span>
            <SoTooltip text="Edit">
              <SoButton
                type="sidebar-icon"
                icon="ellipsis"
                :disabled="current.accountContacts.isFetching"
                @click="openContactModal(contact?.juniferContactId)"
              />
            </SoTooltip>
          </div>
          <div
            v-if="contact.email"
            data-testid="sp-secondary-email"
            class="d-flex justify-space-between flex-wrap content-spacing"
          >
            <div class="d-inline-block text-truncate">
              {{ contact.email }}
            </div>
            <div class="d-flex">
              <SoCopyToClipboard :plain-text="contact.email">
                <template #button="{ copyToClipboard }">
                  <SoButton
                    type="sidebar-icon"
                    icon="copy sm"
                    @click="copyToClipboard"
                  />
                </template>
              </SoCopyToClipboard>
              <SoTooltip text="Email delivery status">
                <SoButton
                  type="sidebar-icon"
                  icon="message-processing sm"
                  data-testid="open-mandrill-modal-btn"
                  @click="openMandrillEmailModal(contact.email ?? '')"
                />
              </SoTooltip>
            </div>
          </div>
          <CustomerPhoneNumberList
            :phone-numbers="[contact?.phone1, contact?.phone2]"
          />
        </template>
        <div class="mt-3 d-flex">
          <SoButton
            title="Add Contact"
            prepend-icon="plus"
            class="mr-1"
            :disabled="current.accountContacts.isFetching"
            @click="openContactModal()"
          />
          <SoTooltip
            v-if="isProtectedContact(contact)"
            text="This contact cannot be deleted directly. Use Manage bill delivery methods to remove paper bills."
          >
            <SoButton icon="delete" :disabled="true" />
          </SoTooltip>
          <SoButton
            v-else
            icon="delete"
            :disabled="current.accountContacts.isFetching"
            @click="openContactRemoveModal(contact.juniferContactId)"
          />
        </div>
      </SoExpansionPanel>
    </SoExpansionPanels>
    <div class="mx-2 mt-5">
      <h6 class="font-weight-medium d-flex align-center">
        <SoIcon icon="home" size="small" class="d-flex mr-1" />
        Supply Address
      </h6>
      <div data-testid="sp-supply-address" class="mt-3 mb-7">
        <SoAddressMap :address="supplyAddress">
          <SoDataTable
            :headers="addressAccountHeaders"
            :items="supplyAddressAccounts"
            :has-pagination="false"
            :searchable="false"
            :loading="current.supplyAddressAccounts.isFetching"
            show-copy-to-clipboard
            class="mb-4"
            row-clickable
            @click:row="openAccount"
          />
        </SoAddressMap>
      </div>
      <h6 class="font-weight-medium d-flex align-center">
        <SoIcon icon="file-text" size="small" class="d-flex mr-1" />
        Billing Address
      </h6>
      <div data-testid="sp-billing-address" class="mt-3 mb-8">
        <SoAddressMap :address="billingAddress"></SoAddressMap>
      </div>
    </div>
  </template>

  <PsrModal v-model="isPsrModalOpen" />

  <ManageBillDeliveryModal v-model="isManageBillDeliveryModalOpen" />
  <ContactDetailsModal
    v-model="isContactDetailsModalOpen"
    :gdpr-fields="gdprFields"
    :junifer-contact-id="selectedJuniferContactId"
  />
  <ContactRemoveModal
    v-model="isContactRemoveModalOpen"
    :junifer-contact-id="selectedJuniferContactId"
  />
  <MandrillEmailModal
    v-model="isMandrillEmailModalOpen"
    :email="selectedEmail"
    data-testid="mandrill-email-modal"
  />
  <SubmitReadingsModal v-model="isSubmitReadingModalOpen" />
  <CancelSwitchModal v-model="isCancelSwitchModalOpen" />
  <UnderReviewPeriodsModal v-model="isReviewPeriodsModalOpen" />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoExpansionPanel from "@/components/expansionPanels/SoExpansionPanel.vue"
  import SoExpansionPanels from "@/components/expansionPanels/SoExpansionPanels.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoAddressMap from "@/components/SoAddressMap.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import CustomerPhoneNumberList from "./components/CustomerPhoneNumberList.vue"
  import OnlineAccountEmail from "./components/OnlineAccountEmail.vue"
  import PsrModal from "@/views/customer/overview/modals/PsrModal.vue"
  import ContactRemoveModal from "@/views/customer/overview/modals/ContactRemoveModal.vue"
  import ContactDetailsModal from "@/views/customer/overview/modals/ContactDetailsModal.vue"
  import ManageBillDeliveryModal from "@/views/customer/overview/modals/ManageBillDeliveryModal.vue"
  import MandrillEmailModal from "@/views/customer/overview/modals/MandrillEmailModal.vue"
  import SubmitReadingsModal from "@/views/customer/meterpoints/modals/SubmitReadingsModal.vue"
  import CancelSwitchModal from "@/views/customer/overview/modals/CancelSwitchModal.vue"
  import ConfirmGdprDetails from "@/views/customer/overview/components/ConfirmGdprDetails.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IAccount } from "@/models/account/account.types"
  import { IUser } from "@/models/admin/user/user.types"
  import {
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { formatDate } from "@/helpers/dateHelper"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { RouteNames } from "@/types/router"
  import CustomerAccountInfo from "@/views/customer/overview/components/CustomerAccountInfo.vue"
  import UnderReviewPeriodsModal from "@/views/customer/overview/modals/UnderReviewPeriodsModal.vue"
  import { IPsrCustomerService } from "@/models/psr/psr.types"
  import { UserService } from "@/services/admin/UserService"
  import { AuthService } from "@/services/AuthService"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { ROLES, Role } from "@/services/PermissionsService"
  import { IPaperBillType } from "@/services/PaperBillService"

  const BILL_DELIVERY_ROLES: ReadonlySet<Role> = new Set([
    ROLES.CCA,
    ROLES.CCA_SNR,
    ROLES.CCA_TL,
    ROLES.BILLING,
    ROLES.DEVELOPER,
  ])

  const PAPER_BILL_TYPE_LABELS: Record<IPaperBillType, string> = {
    STANDARD: "Standard",
    LARGE_PRINT: "Large print",
    BRAILLE: "Braille",
  }

  export default defineComponent({
    components: {
      UnderReviewPeriodsModal,
      CustomerAccountInfo,
      SoButton,
      SoIcon,
      SoTooltip,
      SoExpansionPanel,
      SoExpansionPanels,
      SoDivider,
      SoAddressMap,
      SoDataTable,
      SoCopyToClipboard,
      CustomerPhoneNumberList,
      OnlineAccountEmail,
      PsrModal,
      ConfirmGdprDetails,
      ContactDetailsModal,
      ContactRemoveModal,
      ManageBillDeliveryModal,
      MandrillEmailModal,
      SubmitReadingsModal,
      CancelSwitchModal,
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        RemoveContactMutator: useCurrentAccountStore().removeContact(),
        OpenInJuniferMutator: useCurrentAccountStore().openJuniferUI(),
        isPsrModalOpen: false,
        isSigningIn: false,
        isContactDetailsModalOpen: false,
        isContactRemoveModalOpen: false,
        isManageBillDeliveryModalOpen: false,
        isMandrillEmailModalOpen: false,
        isSubmitReadingModalOpen: false,
        selectedEmail: "",
        selectedJuniferContactId: undefined as number | undefined,
        isCancelSwitchModalOpen: false,
        isReviewPeriodsModalOpen: false,
        addressAccountHeaders: [
          {
            key: "accountNumber",
            title: "Account",
          },
          {
            key: "from",
            title: "From",
          },
          {
            key: "to",
            title: "To",
          },
        ] as SoDataTableHeader[],
        formatDate,
        formatMoney,
        RouteNames,
      }
    },
    computed: {
      psrAlert(): string {
        const vulnerabilities =
          this.current.customerSettings.data?.customerVulnerabilities
        const services = this.current.customerSettings.data?.customerServices

        if (vulnerabilities?.length === 1) {
          return `${vulnerabilities[0].label} (${services.length} priority services)`
        } else if (vulnerabilities?.length > 1) {
          return `${vulnerabilities?.length} vulnerabilities (${services?.length} priority services)`
        } else {
          return ""
        }
      },
      isPsrAlertLoading(): boolean {
        return this.current.customerSettings.isFetching
      },
      psrServicesList() {
        return this.current.customerSettings.data.customerServices
      },
      commsDelivery(): string {
        const comms: string[] = []
        this.primaryContact?.receiveEmail && comms.push("Email")
        this.primaryContact?.receivePost && comms.push("Post")
        this.primaryContact?.receiveSMS && comms.push("SMS")
        return comms.join(", ")
      },
      billingDelivery(): string {
        const methods: string[] = []
        if (this.primaryContact?.billDelivery) {
          methods.push(this.primaryContact.billDelivery)
        }
        const paperBillType = this.current.paperBillState.data?.paperBillType
        if (paperBillType) {
          methods.push(`Post (${PAPER_BILL_TYPE_LABELS[paperBillType]})`)
        }
        return methods.join(", ")
      },
      supplyAddress(): string {
        return this.current.account.data.supplyAddress?.formattedAddress || ""
      },
      supplyAddressAccounts(): SoDataTableItem[] {
        return this.current.supplyAddressAccounts.data.map(
          (account: IAccount) => ({
            accountNumber: account.number,
            from: formatDate(account.startDate),
            to: account.endDate ? formatDate(account.endDate) : "-",
            color: account.endDate ? undefined : "success",
          }),
        )
      },
      billingAddress() {
        return this.current.account.data.billingAddress?.formattedAddress
      },
      primaryContact() {
        return this.current.accountContacts.data.find(
          (contact) => contact.juniferPrimaryContact,
        )
      },
      onlineAccountUsers(): IUser[] {
        return this.current.onlineAccountUsers.data ?? []
      },
      isOnlineAccountLoading(): boolean {
        return this.current.onlineAccountUsers.isFetching
      },
      secondaryContacts() {
        return this.current.accountContacts.data.filter(
          (contact) => !contact.juniferPrimaryContact,
        )
      },
      showCancelSwitchButton() {
        return this.current.meterpoints.data.some(
          (meterpoint) => meterpoint.isWithinCancellationPeriod,
        )
      },
      gdprFields() {
        return this.current.gdprFields.data || []
      },
      canManageBillDelivery(): boolean {
        const roles = AuthService.getUserRoles()
        return roles.some((role) => BILL_DELIVERY_ROLES.has(role))
      },
      manageBillDelivery(): { disabled: boolean; tooltip: string } {
        if (this.primaryContact?.isOccupier) {
          return {
            disabled: true,
            tooltip:
              "Use the Moving Home tool to edit contact details for an Occupier account",
          }
        }
        if (this.current.paperBillState.data?.psrPaperBillEligible) {
          return {
            disabled: true,
            tooltip:
              "Use the Priority Services area to edit the bill delivery methods for a PSR account",
          }
        }
        return { disabled: false, tooltip: "" }
      },
    },
    methods: {
      async openInJunifer() {
        try {
          await this.OpenInJuniferMutator.mutateAsync()
        } catch (error) {
          errorHandler.showCaughtError(
            error,
            "Couldn't open this account in Junifer. Please try again.",
          )
        }
      },
      psrServiceTitle(service: IPsrCustomerService): string {
        return service.alwaysOn ? `${service.label} (always on)` : service.label
      },
      isProtectedContact(contact: { email?: string | null }): boolean {
        const reservedEmails =
          this.current.paperBillState.data?.reservedPaperBillContactEmails ?? []
        return !!contact.email && reservedEmails.includes(contact.email)
      },
      openContactModal(id?: number) {
        this.selectedJuniferContactId = id
        this.isContactDetailsModalOpen = true
      },
      openContactRemoveModal(id: number) {
        this.selectedJuniferContactId = id
        this.isContactRemoveModalOpen = true
      },
      openMandrillEmailModal(email: string) {
        this.selectedEmail = email
        this.isMandrillEmailModalOpen = true
      },
      openReviewPeriodsModal() {
        this.isReviewPeriodsModalOpen = true
      },
      openAccount(row) {
        const routeData = this.$router.resolve({
          name: RouteNames.CustomerOverview,
          params: { accountNumber: row.accountNumber },
        })
        window.open(routeData.href, "_blank")
      },
      async signInToMyAccount() {
        this.isSigningIn = true
        try {
          const accountId = this.current.coreId.data
          if (import.meta.env.DEV)
            throw new Error("Auto sign in is disabled in dev")

          if (!accountId) throw new Error("Account ID is missing")
          const users = (
            await UserService.getUsersForAccount(accountId).suspense?.()
          )?.data

          if (!users?.length) throw new Error("Account has no users")
          await UserService.signInToMyAccount(users[0].id, users[0].email)
        } catch (e) {
          console.error(e)
          this.$router.push({
            name: RouteNames.AdminUserList,
            query: { email: this.primaryContact?.email },
          })
        } finally {
          this.isSigningIn = false
        }
      },
    },
  })
</script>

<style lang="css" scoped>
  .sp-button-grid {
    display: inline-grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }

  .sp-button-grid :nth-child(even) {
    text-align: start;
    grid-column: span 2;
  }

  .sp-button-grid :nth-child(odd) {
    grid-column: span 2;
  }

  .sp-button-grid :nth-last-child(1):nth-child(odd) {
    grid-column: 2 / span 2;
    text-align: center;
  }

  .sp-psr-alert {
    display: inline-flex;
    align-items: center;
    text-align: start;
    word-break: break-word;
    gap: 8px;
  }

  .content-spacing {
    line-height: 2;
  }
</style>
